<script setup lang="ts">
import { ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import { EMIT } from '@/enum';
import { useBuildingStore } from '@/store';

interface OrganizationBuildingInterface {
  id: number;
  organization_id: number;
  name: string;
  location: string | null;
}

type OrganizationBuilding = OrganizationBuildingInterface;

type SelectedOrganizationBuilding = OrganizationBuildingInterface & {
  building_id: number;
};

type EditableOrganizationBuilding = OrganizationBuildingInterface & {
  checked: boolean;
};

const emit = defineEmits([EMIT.CANCEL, EMIT.ATTACH, EMIT.CLOSE, EMIT.DETACH, EMIT.UPDATE_MODEL_VALUE]);

const buildingStore = useBuildingStore();
const props = defineProps<{
  modelValue: boolean;
  selectedBuildings: SelectedOrganizationBuilding[];
  organizationBuildings: OrganizationBuilding[];
  organizationName: string;
}>();

const visible = ref(props.modelValue);

watch(
  () => props.modelValue,
  (val) => {
    visible.value = val;
  },
);

watch(visible, (val: boolean) => {
  if (!val) {
    emit(EMIT.CANCEL);
    emit(EMIT.UPDATE_MODEL_VALUE, val);
  }
});

const editableBuildings = ref<EditableOrganizationBuilding[]>([]);

const originalSelectedIds = ref<Set<number>>(new Set());

watch(
  [() => props.organizationBuildings, () => props.selectedBuildings],
  ([orgBuildings, selected]) => {
    const selectedIds = new Set(selected.map((b) => b.building_id));
    originalSelectedIds.value = selectedIds;
    editableBuildings.value = orgBuildings.map((b) => ({
      ...b,
      checked: selectedIds.has(b.id),
    }));
  },
  { immediate: true },
);

const close = () => {
  visible.value = false;
};

const save = () => {
  const toAttach: number[] = [];
  const toDetach: number[] = [];

  for (const building of editableBuildings.value) {
    const wasSelected = originalSelectedIds.value.has(building.id);
    const isNowChecked = building.checked;

    if (isNowChecked && !wasSelected) {
      toAttach.push(building.id);
    } else if (!isNowChecked && wasSelected) {
      toDetach.push(building.id);
    }
  }

  if (toAttach.length === 0 && toDetach.length === 0) {
    buildingStore.doToast('No changes made to buildings', '', true);
    visible.value = false;
    return;
  }

  if (toAttach.length > 0) emit(EMIT.ATTACH, toAttach);
  if (toDetach.length > 0) emit(EMIT.DETACH, toDetach);

  visible.value = false;
};
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    header="Edit Buildings"
    :closable="true"
    style="width: 500px"
    :draggable="false"
  >
    <h2 class="mb-2 text-lg font-semibold">
      {{ organizationName || 'Organization' }}
    </h2>

    <p class="mb-4 text-sm text-gray-600">Select the buildings to assign for this organization:</p>

    <ul class="space-y-2 max-h-64 overflow-y-auto">
      <li v-for="building in editableBuildings" :key="building.id" class="flex items-center gap-2">
        <Checkbox v-model="building.checked" :input-id="'building-' + building.id" :binary="true" />
        <label :for="'building-' + building.id" class="cursor-pointer">
          {{ building.name }}
        </label>
      </li>
    </ul>

    <template #footer>
      <AppButton text="Cancel" type="secondary" @click="close" />
      <AppButton text="Save" type="primary" @click="save" />
    </template>
  </Dialog>
</template>
